import React, { useState, useEffect } from "react";
import { Youtube, Upload } from "lucide-react";
import { getFirestore, doc, getDoc, updateDoc } from "firebase/firestore";
import Swal from "sweetalert2";
import { useAuth } from "../AuthContext"; // Assuming you have an auth context
import { ClipLoader } from "react-spinners"; // Loader for checking status

const YouTubeTask = () => {
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const db = getFirestore();
  const { currentUser } = useAuth(); // Get the current logged-in user

  // Check the task completion status for the current user
  useEffect(() => {
    const fetchTaskStatus = async () => {
      if (!currentUser) return;

      try {
        const userDocRef = doc(db, "users", currentUser.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists() && userDoc.data()?.youtubeTaskCompleted) {
          setIsCompleted(true);
        }
      } catch (error) {
        console.error("Error fetching task status:", error);
      } finally {
        setLoading(false); // Stop the loader
      }
    };

    fetchTaskStatus();
  }, [currentUser, db]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files?.[0]) {
      setScreenshot(event.target.files[0]);
    }
  };

  const handleSubmit = async () => {
    if (!currentUser) {
      alert("You must be logged in to submit a screenshot.");
      return;
    }

    if (!screenshot) {
      alert("Please upload a screenshot before submitting.");
      return;
    }

    const result = await Swal.fire({
      title: "Warning!",
      text: "Your screenshot will be manually verified. If found fake, all your earnings will be forfeited. Do you wish to proceed?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Proceed",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      const userDocRef = doc(db, "users", currentUser.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        alert("User document does not exist.");
        return;
      }

      // Check if the task is already marked as completed in Firestore
      if (userDoc.data()?.youtubeTaskCompleted) {
        Swal.fire({
          title: "Task Already Completed",
          text: "You have already submitted this task.",
          icon: "info",
        });
        setIsCompleted(true);
        return;
      }

      const currentScore = userDoc.data()?.score || 0;

      // Update Firestore: Save task status and increment score
      await updateDoc(userDocRef, {
        score: currentScore + 100, // Increment score by 100
        youtubeTaskCompleted: true, // Mark task as completed
      });

      Swal.fire({
        title: "Success!",
        text: "Screenshot submitted successfully! You've been rewarded 100 points.",
        icon: "success",
      });

      setIsCompleted(true);
      setScreenshot(null); // Reset file input
    } catch (error) {
      console.error("Error updating task:", error);
      Swal.fire({
        title: "Error!",
        text: "An error occurred. Please try again.",
        icon: "error",
      });
    }
  };

  return (
    <div
      className="h-screen overflow-y-auto p-4" // Full height and scrollable
      style={{ paddingBottom: "10px" }} // Prevent content from being hidden by bottom navigation
    >
      {loading ? (
        <div className="flex justify-center items-center">
          <ClipLoader size={30} color="#3B82F6" />
        </div>
      ) : (
        <>
          <div
          
            onClick={() => {
              if (!isCompleted) {
                window.open("https://www.youtube.com/@Iqrewardug", "_blank");
              }
            }}
            className={`p-3 ${
              isCompleted ? "bg-gray-600 cursor-not-allowed" : "bg-gray-800 hover:bg-gray-700"
            } rounded-lg shadow-md flex justify-between items-center transition-all duration-200`}
          >
            <Youtube size={24} className="text-red-500" />
            <p className="font-medium text-gray-100 text-sm">
              {isCompleted ? "Task Completed" : "Subscribe to our YouTube Channel"}
            </p>
            <span className="text-gray-400">{isCompleted ? "" : "Earn 100 Points"}</span>
          </div>

          {!isCompleted && (
            <div className="flex items-center space-x-3">
              <label
                htmlFor="screenshot"
                className="text-sm text-center text-gray-300 bg-blue-600 hover:bg-blue-700 rounded-lg py-1 px-3 cursor-pointer"
              >
                <Upload size={16} className="inline-block mr-1" />
                Upload
              </label>
              <input
                id="screenshot"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileChange}
              />
              <button
                className="bg-green-500 text-white text-sm px-3 py-1 rounded-lg hover:bg-green-600 transition-all"
                onClick={handleSubmit}
              >
                Submit
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default YouTubeTask;
